import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CancelOutlined as RejectIcon,
  CheckCircleOutline as ApproveIcon,
  DescriptionOutlined as FileIcon,
  Refresh as RefreshIcon,
  ScheduleOutlined as ScheduleIcon,
  SendOutlined as SendIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useSelector } from 'react-redux';

import AnimatedPage from '@/components/AnimatedPage';
import { hasPermission, Permission } from '@/auth/permissions';
import axiosInstance from '@/services/api/axios';
import type { RootState } from '@/store';


interface RefundFile {
  id: string;
  document_type: string;
  filename: string;
  content_type: string;
  size: number;
}

interface BalanceCredit {
  id: string;
  amount: string;
  old_balance: string;
  new_balance: string;
  created_at: string;
}

interface RefundRequest {
  id: string;
  trader_id: string;
  trader_name?: string;
  transaction_id: string;
  invoice_id?: string;
  tracking_id: string;
  request_type: 'REFUND' | 'CHARGEBACK' | 'CYBER_COMPLIANCE';
  payer_name?: string;
  payer_phone?: string;
  payer_email?: string;
  client_transaction_id?: string;
  client_user_id?: string;
  utr?: string;
  amount: string;
  currency_code: string;
  comment?: string;
  incident_received_at: string;
  submission_deadline: string;
  is_late: boolean;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  review_comment?: string;
  reviewer_name?: string;
  reviewed_at?: string;
  manual_chat_sent_at?: string | null;
  manual_chat_sender_name?: string | null;
  created_at: string;
  files: RefundFile[];
  balance_credit?: BalanceCredit | null;
}

type RefundDecision = 'APPROVED' | 'REJECTED';

interface DecisionDialogState {
  request: RefundRequest;
  decision: RefundDecision;
}

interface RefundFilePreview {
  filename: string;
  kind: 'image' | 'pdf';
  url: string;
}

const PAGE_SIZE = 25;

const statusColor = (value: RefundRequest['status']): 'warning' | 'success' | 'error' => {
  if (value === 'APPROVED') return 'success';
  if (value === 'REJECTED') return 'error';
  return 'warning';
};

const requestError = (error: unknown, fallback: string) =>
  axios.isAxiosError(error) ? error.response?.data?.detail || error.message : fallback;

const requestTypeLabel = (value: RefundRequest['request_type']) => ({
  REFUND: 'Refund',
  CHARGEBACK: 'Chargeback',
  CYBER_COMPLIANCE: 'Cyber Compliance',
})[value];

const isPdfFile = (file: RefundFile, blob: Blob) =>
  String(blob.type || file.content_type).toLowerCase().includes('application/pdf')
  || file.filename.toLowerCase().endsWith('.pdf');

const statusLabel = (value: RefundRequest['status']) => ({
  PENDING: 'На проверке',
  APPROVED: 'Одобрено',
  REJECTED: 'Отклонено',
})[value];

const formatDate = (value?: string) => value ? new Date(value).toLocaleString() : '—';

export default function RefundRequests() {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const canManage = hasPermission(currentUser, Permission.REFUND_REQUESTS_MANAGE);
  const [items, setItems] = useState<RefundRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [lateOnly, setLateOnly] = useState(false);
  const [selected, setSelected] = useState<RefundRequest | null>(null);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [sendingToChatId, setSendingToChatId] = useState<string | null>(null);
  const [markingChatId, setMarkingChatId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [decisionDialog, setDecisionDialog] = useState<DecisionDialogState | null>(null);
  const [decisionComment, setDecisionComment] = useState('');
  const [openingFileId, setOpeningFileId] = useState<string | null>(null);
  const [filePreview, setFilePreview] = useState<RefundFilePreview | null>(null);

  useEffect(() => () => {
    if (filePreview?.url) URL.revokeObjectURL(filePreview.url);
  }, [filePreview?.url]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/refund-requests', {
        params: {
          page,
          page_size: PAGE_SIZE,
          search: search.trim() || undefined,
          status: statusFilter || undefined,
          request_type: typeFilter || undefined,
          late_only: lateOnly || undefined,
        },
      });
      setItems(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (requestFailure) {
      setError(requestError(requestFailure, 'Не удалось загрузить заявки на возврат'));
    } finally {
      setLoading(false);
    }
  }, [lateOnly, page, search, statusFilter, typeFilter]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 300);
    return () => window.clearTimeout(timer);
  }, [load]);

  const openDecisionDialog = (request: RefundRequest, decision: RefundDecision) => {
    setDecisionComment('');
    setDecisionDialog({ request, decision });
  };

  const closeDecisionDialog = () => {
    if (processingId) return;
    setDecisionDialog(null);
    setDecisionComment('');
  };

  const decide = async () => {
    if (!decisionDialog) return;
    const { request, decision } = decisionDialog;
    const comment = decisionComment.trim();
    if (decision === 'REJECTED' && !comment) return;

    const row = request;
    setProcessingId(row.id);
    try {
      await axiosInstance.post(`/refund-requests/${row.id}/decision`, {
        status: decision,
        comment: comment || null,
      });
      setDecisionDialog(null);
      setDecisionComment('');
      setSelected(null);
      await load();
    } catch (requestFailure) {
      setError(requestError(requestFailure, 'Не удалось обработать заявку'));
    } finally {
      setProcessingId(null);
    }
  };

  const openFile = async (file: RefundFile) => {
    setOpeningFileId(file.id);
    try {
      const response = await axiosInstance.get(`/refund-requests/files/${file.id}/download`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      setFilePreview({
        filename: file.filename,
        kind: isPdfFile(file, response.data) ? 'pdf' : 'image',
        url,
      });
    } catch (requestFailure) {
      setError(requestError(requestFailure, 'Не удалось открыть файл'));
    } finally {
      setOpeningFileId(null);
    }
  };

  const sendToManualChat = async (request: RefundRequest) => {
    setSendingToChatId(request.id);
    setError(null);
    setSuccess(null);
    try {
      await axiosInstance.post(`/refund-requests/${request.id}/send-to-manual-chat`);
      setSuccess('Запрос на отправку принят. Проверьте чат и поставьте отметку «Отправлено в чат».');
    } catch (requestFailure) {
      setError(requestError(requestFailure, 'Не удалось отправить заявку в мануальный чат'));
    } finally {
      setSendingToChatId(null);
    }
  };

  const markManualChat = async (request: RefundRequest, sent: boolean) => {
    setMarkingChatId(request.id);
    try {
      const { data } = await axiosInstance.patch<RefundRequest>(`/refund-requests/${request.id}/manual-chat-sent`, { sent });
      setItems((current) => current.map((item) => item.id === data.id ? data : item));
      setSelected((current) => current?.id === data.id ? data : current);
    } catch (requestFailure) {
      setError(requestError(requestFailure, 'Не удалось сохранить отметку'));
    } finally {
      setMarkingChatId(null);
    }
  };

  const chatCheckbox = (request: RefundRequest) => (
    <Tooltip title={request.manual_chat_sent_at
      ? `${request.manual_chat_sender_name || 'Сотрудник'} · ${formatDate(request.manual_chat_sent_at)}`
      : 'Отметьте после отправки в чат, в том числе вручную'}>
      <FormControlLabel
        sx={{ m: 0 }}
        control={<Checkbox color="success" checked={Boolean(request.manual_chat_sent_at)} disabled={!canManage || Boolean(markingChatId)} onChange={(_, sent) => void markManualChat(request, sent)} />}
        label={request.manual_chat_sent_at ? 'Отправлено в чат' : 'Отправлено в чат?'}
      />
    </Tooltip>
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const repairingBalance = Boolean(
    decisionDialog?.decision === 'APPROVED'
    && decisionDialog.request.status === 'APPROVED'
    && !decisionDialog.request.balance_credit,
  );

  return (
    <AnimatedPage>
      <Box>
        <Box display="flex" alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" gap={2} mb={3}>
          <Box>
            <Typography variant="h4">Споры и возвраты</Typography>
            <Typography color="text.secondary" mt={0.5}>
              Заявки трейдеров на Refund, Chargeback и Cyber Compliance
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<RefreshIcon />} disabled={loading} onClick={() => void load()}>
            Обновить
          </Button>
        </Box>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="Трейдер, UTR, плательщик или ID"
              value={search}
              onChange={(event) => { setSearch(event.target.value); setPage(1); }}
            />
            <FormControl sx={{ minWidth: 190 }}>
              <InputLabel>Статус</InputLabel>
              <Select value={statusFilter} label="Статус" onChange={(event) => { setStatusFilter(event.target.value); setPage(1); }}>
                <MenuItem value="">Все статусы</MenuItem>
                <MenuItem value="PENDING">На проверке</MenuItem>
                <MenuItem value="APPROVED">Одобрено</MenuItem>
                <MenuItem value="REJECTED">Отклонено</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 210 }}>
              <InputLabel>Тип обращения</InputLabel>
              <Select value={typeFilter} label="Тип обращения" onChange={(event) => { setTypeFilter(event.target.value); setPage(1); }}>
                <MenuItem value="">Все типы</MenuItem>
                <MenuItem value="REFUND">Refund</MenuItem>
                <MenuItem value="CHARGEBACK">Chargeback</MenuItem>
                <MenuItem value="CYBER_COMPLIANCE">Cyber Compliance</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="Показать только заявки, созданные позже 24 часов после получения информации об инциденте">
              <Button
                startIcon={<ScheduleIcon />}
                variant={lateOnly ? 'contained' : 'outlined'}
                color="warning"
                sx={{
                  minWidth: 210,
                  whiteSpace: 'nowrap',
                  bgcolor: lateOnly ? undefined : 'rgba(255, 167, 38, 0.06)',
                  '&:hover': {
                    bgcolor: lateOnly ? undefined : 'rgba(255, 167, 38, 0.12)',
                  },
                }}
                onClick={() => { setLateOnly((value) => !value); setPage(1); }}
              >
                Только просроченные
              </Button>
            </Tooltip>
            <Button
              color="inherit"
              onClick={() => { setSearch(''); setStatusFilter(''); setTypeFilter(''); setLateOnly(false); setPage(1); }}
            >
              Сбросить
            </Button>
          </Stack>
        </Paper>

        <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 1180 }}>
            <TableHead>
              <TableRow>
                <TableCell>Создано</TableCell>
                <TableCell>Тип</TableCell>
                <TableCell>Трейдер</TableCell>
                <TableCell>Операция</TableCell>
                <TableCell>Плательщик / UTR</TableCell>
                <TableCell align="right">Сумма</TableCell>
                <TableCell>Срок подачи</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Мануал чат</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={row.manual_chat_sent_at
                    ? { bgcolor: 'rgba(76, 175, 80, 0.10)', '&:hover': { bgcolor: 'rgba(76, 175, 80, 0.16)' } }
                    : row.status === 'PENDING' ? { bgcolor: 'rgba(255, 167, 38, 0.04)' } : undefined}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.created_at)}</TableCell>
                  <TableCell><Chip size="small" variant="outlined" label={requestTypeLabel(row.request_type)} /></TableCell>
                  <TableCell>{row.trader_name || row.trader_id}</TableCell>
                  <TableCell sx={{ maxWidth: 260 }}>
                    <Typography variant="body2" noWrap title={row.transaction_id} sx={{ fontFamily: 'monospace' }}>
                      TX: {row.transaction_id}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block" title={row.tracking_id} sx={{ fontFamily: 'monospace' }}>
                      Tracking: {row.tracking_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {row.payer_name || '—'}
                    <Typography variant="caption" color="text.secondary" display="block">UTR: {row.utr || '—'}</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {row.amount} {row.currency_code}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Срок считается от момента получения информации об инциденте до создания заявки">
                      <Chip
                        size="small"
                        color={row.is_late ? 'error' : 'success'}
                        variant={row.is_late ? 'filled' : 'outlined'}
                        label={row.is_late ? 'Подано позже 24 ч' : 'Подано в течение 24 ч'}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell><Chip size="small" color={statusColor(row.status)} label={statusLabel(row.status)} /></TableCell>
                  <TableCell sx={{ minWidth: 190 }}>{chatCheckbox(row)}</TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="outlined" onClick={() => setSelected(row)}>Открыть</Button>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    {loading ? 'Загрузка заявок…' : 'Заявок не найдено'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Stack alignItems="center" mt={3}>
            <Pagination
              count={totalPages}
              page={page}
              color="primary"
              showFirstButton
              showLastButton
              onChange={(_, value) => setPage(value)}
            />
          </Stack>
        )}

        <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="md">
          <DialogTitle sx={{ pb: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" gap={1.5}>
              <Box>
                <Typography variant="h6">Заявка на рассмотрение</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{selected?.id}</Typography>
              </Box>
              {selected && (
                <Stack direction="row" spacing={1}>
                  <Chip size="small" variant="outlined" label={requestTypeLabel(selected.request_type)} />
                  <Chip size="small" color={statusColor(selected.status)} label={statusLabel(selected.status)} />
                </Stack>
              )}
            </Stack>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            {selected && (
              <Stack spacing={2.5}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary">Трейдер</Typography>
                    <Typography mt={0.5} fontWeight={600}>{selected.trader_name || selected.trader_id}</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary">Сумма</Typography>
                    <Typography mt={0.5} fontWeight={700}>{selected.amount} {selected.currency_code}</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary">Срок подачи</Typography>
                    <Box mt={0.75}>
                      <Tooltip title="Срок считается от момента получения информации об инциденте до создания заявки">
                        <Chip
                          size="small"
                          color={selected.is_late ? 'error' : 'success'}
                          variant={selected.is_late ? 'filled' : 'outlined'}
                          label={selected.is_late ? 'Подано позже 24 ч' : 'Подано в течение 24 ч'}
                        />
                      </Tooltip>
                    </Box>
                  </Paper>
                </Box>

                <Box>
                  <Typography variant="subtitle1" fontWeight={600} mb={1.5}>Данные операции</Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    {[
                      ['Invoice ID', selected.invoice_id || '—'],
                      ['Transaction ID', selected.transaction_id],
                      ['Tracking ID', selected.tracking_id],
                      ['Client Transaction ID', selected.client_transaction_id || '—'],
                      ['Client User ID', selected.client_user_id || '—'],
                    ].map(([label, value], index) => (
                      <Box key={label}>
                        {index > 0 && <Divider sx={{ my: 1.5 }} />}
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                        <Typography variant="body2" mt={0.5} sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>{value}</Typography>
                      </Box>
                    ))}
                  </Paper>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Плательщик</Typography>
                    <Typography mt={0.5}>{selected.payer_name || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Телефон плательщика</Typography>
                    <Typography mt={0.5}>{selected.payer_phone || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Почта плательщика</Typography>
                    <Typography mt={0.5} sx={{ overflowWrap: 'anywhere' }}>{selected.payer_email || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">UTR</Typography>
                    <Typography mt={0.5} sx={{ fontFamily: 'monospace' }}>{selected.utr || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Информация получена</Typography>
                    <Typography mt={0.5}>{formatDate(selected.incident_received_at)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Заявка подана</Typography>
                    <Typography mt={0.5}>{formatDate(selected.created_at)}</Typography>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle1" fontWeight={600} mb={1}>Описание</Typography>
                  <Typography color={selected.comment ? 'text.primary' : 'text.secondary'} sx={{ whiteSpace: 'pre-wrap' }}>
                    {selected.comment || 'Описание не добавлено'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle1" fontWeight={600} mb={1}>Документы</Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {selected.files.map((file) => (
                      <Button
                        key={file.id}
                        variant="outlined"
                        size="small"
                        startIcon={<FileIcon />}
                        disabled={openingFileId === file.id}
                        onClick={() => void openFile(file)}
                      >
                        {file.filename}
                      </Button>
                    ))}
                    {selected.files.length === 0 && <Typography color="text.secondary">Документы не приложены</Typography>}
                  </Stack>
                </Box>

                {selected.review_comment && (
                  <Alert severity={selected.status === 'REJECTED' ? 'error' : 'success'}>
                    <Typography variant="body2" fontWeight={600} mb={0.5}>Комментарий проверяющего</Typography>
                    {selected.review_comment}
                  </Alert>
                )}
                {selected.status === 'APPROVED' && (
                  selected.balance_credit
                    ? (
                      <Alert severity="success">
                        На рабочий баланс зачислено {selected.balance_credit.amount} {selected.currency_code}.
                        Баланс после операции: {selected.balance_credit.new_balance} {selected.currency_code}.
                      </Alert>
                    )
                    : (
                      <Alert severity="warning">
                        Заявка одобрена, но проводка по балансу отсутствует. Нажмите «Зачислить на баланс» для безопасного восстановления.
                      </Alert>
                    )
                )}
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
            {selected && canManage && chatCheckbox(selected)}
            <Button color="inherit" onClick={() => setSelected(null)}>Закрыть</Button>
            {selected && canManage && (
              <Button
                variant="outlined"
                startIcon={<SendIcon />}
                disabled={sendingToChatId === selected.id}
                onClick={() => void sendToManualChat(selected)}
              >
                {sendingToChatId === selected.id
                  ? 'Отправка…'
                  : 'Отправить в мануальный чат'}
              </Button>
            )}
            {canManage && selected?.status === 'PENDING' && (
              <>
                <Button
                  color="error"
                  variant="outlined"
                  startIcon={<RejectIcon />}
                  disabled={processingId === selected.id}
                  onClick={() => openDecisionDialog(selected, 'REJECTED')}
                >
                  Отклонить
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<ApproveIcon />}
                  disabled={processingId === selected.id}
                  onClick={() => openDecisionDialog(selected, 'APPROVED')}
                >
                  Одобрить
                </Button>
              </>
            )}
            {canManage && selected?.status === 'APPROVED' && !selected.balance_credit && (
              <Button
                variant="contained"
                color="success"
                startIcon={<ApproveIcon />}
                disabled={processingId === selected.id}
                onClick={() => openDecisionDialog(selected, 'APPROVED')}
              >
                Зачислить на баланс
              </Button>
            )}
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(filePreview)}
          onClose={() => setFilePreview(null)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>{filePreview?.filename}</DialogTitle>
          <DialogContent dividers sx={{ p: 2, minHeight: 320 }}>
            {filePreview?.kind === 'image' && (
              <Box
                component="img"
                src={filePreview.url}
                alt={filePreview.filename}
                sx={{
                  display: 'block',
                  maxWidth: '100%',
                  maxHeight: '75vh',
                  mx: 'auto',
                  objectFit: 'contain',
                }}
              />
            )}
            {filePreview?.kind === 'pdf' && (
              <Box
                component="iframe"
                src={filePreview.url}
                title={filePreview.filename}
                sx={{
                  display: 'block',
                  width: '100%',
                  height: '75vh',
                  border: 0,
                  bgcolor: 'background.default',
                }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFilePreview(null)}>Закрыть</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={Boolean(decisionDialog)} onClose={closeDecisionDialog} fullWidth maxWidth="sm">
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box sx={{ display: 'flex', color: decisionDialog?.decision === 'REJECTED' ? 'error.main' : 'success.main' }}>
                {decisionDialog?.decision === 'REJECTED' ? <RejectIcon /> : <ApproveIcon />}
              </Box>
              <Box>
                <Typography variant="h6">
                  {repairingBalance
                    ? 'Зачислить средства на баланс'
                    : decisionDialog?.decision === 'REJECTED'
                      ? 'Отклонить заявку'
                      : 'Одобрить заявку'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {decisionDialog?.request.trader_name || decisionDialog?.request.trader_id}
                </Typography>
              </Box>
            </Stack>
          </DialogTitle>
          <DialogContent dividers>
            <Alert severity={decisionDialog?.decision === 'REJECTED' ? 'warning' : 'info'} sx={{ mb: 2.5 }}>
              {decisionDialog?.decision === 'REJECTED'
                ? 'Укажите понятную причину: трейдер увидит её в своей заявке.'
                : repairingBalance
                  ? 'Будет создана недостающая проводка. Повторное нажатие не создаст дубликат.'
                  : 'Комментарий необязателен. При необходимости зафиксируйте детали решения.'}
            </Alert>
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={4}
              maxRows={8}
              required={decisionDialog?.decision === 'REJECTED'}
              label={decisionDialog?.decision === 'REJECTED' ? 'Причина отклонения' : 'Комментарий к решению'}
              placeholder={decisionDialog?.decision === 'REJECTED' ? 'Опишите, почему заявка отклонена' : 'Добавьте комментарий, если это необходимо'}
              value={decisionComment}
              onChange={(event) => setDecisionComment(event.target.value)}
              inputProps={{ maxLength: 3000 }}
              error={decisionDialog?.decision === 'REJECTED' && decisionComment.length > 0 && !decisionComment.trim()}
              helperText={`${decisionDialog?.decision === 'REJECTED' ? 'Обязательное поле' : 'Необязательное поле'} · ${decisionComment.length}/3000`}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button color="inherit" disabled={Boolean(processingId)} onClick={closeDecisionDialog}>Отмена</Button>
            <Button
              variant="contained"
              color={decisionDialog?.decision === 'REJECTED' ? 'error' : 'success'}
              startIcon={decisionDialog?.decision === 'REJECTED' ? <RejectIcon /> : <ApproveIcon />}
              disabled={Boolean(processingId) || (decisionDialog?.decision === 'REJECTED' && !decisionComment.trim())}
              onClick={() => void decide()}
            >
              {processingId
                ? 'Сохранение…'
                : repairingBalance
                  ? 'Зачислить на баланс'
                  : decisionDialog?.decision === 'REJECTED'
                    ? 'Отклонить заявку'
                    : 'Одобрить заявку'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={Boolean(error)} autoHideDuration={6000} onClose={() => setError(null)}>
          <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
        </Snackbar>
        <Snackbar open={Boolean(success)} autoHideDuration={5000} onClose={() => setSuccess(null)}>
          <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>
        </Snackbar>
      </Box>
    </AnimatedPage>
  );
}
